import { createLibrary, defineComponent } from "@openuidev/lang-core";
import { z } from "zod";
import { validateProgram, type ValidationOutcome, type WidgetIssue } from "./validate";

// Parent insight cards: the third OpenUI level of the experience layer (ADR-0014).
// A separate library from the tutor's widgets, so the child's cached prompt does not grow
// and a parent card can never appear in a lesson.
//
// Numbers never come from the model. A card names skills and a metric; the parent app
// fills in the figure from the engine's report (`SkillProgress` and `AlertCount` carry
// no number props), and free text may not contain digits at all, so a model cannot slip
// a made-up percentage into a sentence.

/** Text a model may write on a parent card: short, and without any digit. */
const prose = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .regex(/^[^0-9]*$/, "no digits: figures come from the engine");

const SkillProgress = defineComponent({
  name: "SkillProgress",
  description:
    "Shows one skill's figure from the engine report: `mastery` (current level) or `change` (this week's change). You only name the skill id and the metric; the app shows the number.",
  props: z.object({
    skillId: z.string().min(1).max(80),
    metric: z.enum(["mastery", "change"]),
  }),
  component: null,
});

const AlertCount = defineComponent({
  name: "AlertCount",
  description:
    "Shows how many alerts of one kind the engine raised this week. The app fills in the count.",
  props: z.object({
    reason: z.enum(["frustration", "plateau", "daily_cap", "safety"]),
  }),
  component: null,
});

const InsightCard = defineComponent({
  name: "InsightCard",
  description:
    "One card for the parent: a title, one or two sentences of plain explanation, and the skills it is about. Never write numbers; use SkillProgress or AlertCount.",
  props: z.object({
    tone: z.enum(["progress", "attention", "info"]),
    title: prose(80),
    body: prose(400),
    figures: z.array(z.union([SkillProgress.ref, AlertCount.ref])).max(6),
  }),
  component: null,
});

const Cards = defineComponent({
  name: "Cards",
  description: "Root of the parent insight view: one to four cards, most important first.",
  props: z.object({
    cards: z.array(InsightCard.ref).min(1).max(4),
  }),
  component: null,
});

export const PARENT_INSIGHT_LIBRARY_VERSION = "0.1.0";

function build() {
  return createLibrary({
    id: `tutor-parent-insights@${PARENT_INSIGHT_LIBRARY_VERSION}`,
    root: "Cards",
    components: [Cards, InsightCard, SkillProgress, AlertCount],
  });
}

let library: ReturnType<typeof build> | undefined;
let cachedSchema: ReturnType<ReturnType<typeof build>["toJSONSchema"]> | undefined;

/** The parent card library, built on first use (Workers forbid its random id at import). */
export function parentInsightLibrary(): ReturnType<typeof build> {
  library ??= build();
  return library;
}

function schema() {
  cachedSchema ??= parentInsightLibrary().toJSONSchema();
  return cachedSchema;
}

/** System prompt part for generating parent cards, built locally from the library. */
export function parentInsightSystemPrompt(locale: "en" | "fr"): string {
  return parentInsightLibrary().prompt({
    inlineMode: false,
    toolCalls: false,
    bindings: false,
    preamble:
      "You write short, warm insight cards for a parent about their child's week with a tutor. The engine's report is given as skill ids with figures; explain what they mean in plain words.",
    additionalRules: [
      `Write in ${locale === "fr" ? "French" : "English"}.`,
      "Never write a digit or a number in text. Show figures only with SkillProgress or AlertCount.",
      "Only reference skill ids that appear in the report.",
      "Do not judge the child or compare them with other children. Do not give medical or psychological opinions.",
    ],
  });
}

/**
 * Validates a parent card program: the library's schemas (no digits in text) plus the
 * report binding (every skill id must be one the engine reported). Never throws.
 */
export function validateInsightProgram(
  source: string,
  reportedSkills: ReadonlySet<string>,
): ValidationOutcome {
  const outcome = validateProgram(parentInsightLibrary(), schema(), "Cards", source);
  if (outcome.kind !== "valid") return outcome;
  const issues: WidgetIssue[] = [];
  const root = outcome.result.root;
  if (root !== null) collectUnknownSkills(root, reportedSkills, issues);
  return issues.length > 0
    ? { kind: "fallback", reason: "card references a skill not in the report", issues }
    : outcome;
}

function collectUnknownSkills(node: unknown, reported: ReadonlySet<string>, issues: WidgetIssue[]) {
  if (typeof node !== "object" || node === null) return;
  if (Array.isArray(node)) {
    for (const child of node) collectUnknownSkills(child, reported, issues);
    return;
  }
  const element = node as { type?: unknown; typeName?: unknown; props?: unknown };
  if (element.type === "element" && element.typeName === "SkillProgress") {
    const skillId = (element.props as { skillId?: unknown } | undefined)?.skillId;
    if (typeof skillId !== "string" || !reported.has(skillId)) {
      issues.push({ component: "SkillProgress", path: "/skillId", message: "unknown skill" });
    }
    return;
  }
  if (typeof element.props === "object" && element.props !== null) {
    for (const value of Object.values(element.props)) collectUnknownSkills(value, reported, issues);
  }
}

/** Skills ranked by the caller from engine figures; the builder never sees a number. */
export interface InsightRanking {
  /** Skills with the largest positive mastery change, best first. */
  readonly improving: readonly string[];
  /** Practised skills with little or no progress, most attention-worthy first. */
  readonly needsAttention: readonly string[];
  /** Alert kinds raised this week. */
  readonly alerts: readonly ("frustration" | "plateau" | "daily_cap" | "safety")[];
}

const COPY = {
  en: {
    progressTitle: "What moved forward",
    progressBody: "These skills improved the most this week.",
    attentionTitle: "Worth a little more practice",
    attentionBody: "Practice happened here, but progress is slow. Coach will come back to it.",
    alertsTitle: "Alerts this week",
    alertsBody: "Coach told you about these moments during the week.",
    quietTitle: "A quiet week",
    quietBody: "There is not enough practice yet to say how things are going.",
  },
  fr: {
    progressTitle: "Ce qui a avancé",
    progressBody: "Ces compétences ont le plus progressé cette semaine.",
    attentionTitle: "À retravailler un peu",
    attentionBody:
      "Il y a eu de l'entraînement ici, mais les progrès sont lents. Coach y reviendra.",
    alertsTitle: "Alertes de la semaine",
    alertsBody: "Coach vous a signalé ces moments pendant la semaine.",
    quietTitle: "Une semaine calme",
    quietBody: "Il n'y a pas encore assez d'entraînement pour faire un bilan.",
  },
} as const;

/**
 * Deterministic cards from the engine's ranking, without a model: the default view and
 * the fallback whenever a generated program fails validation.
 */
export function insightProgramFromRanking(ranking: InsightRanking, locale: "en" | "fr"): string {
  const t = COPY[locale];
  const lines: string[] = [];
  const cards: string[] = [];
  const card = (name: string, tone: string, title: string, body: string, figures: string[]) => {
    cards.push(name);
    lines.push(
      `${name} = InsightCard(${q(tone)}, ${q(title)}, ${q(body)}, [${figures.join(", ")}])`,
    );
  };
  const skillFigures = (prefix: string, ids: readonly string[], metric: string) =>
    ids.slice(0, 3).map((id, i) => {
      const name = `${prefix}${String(i)}`;
      lines.push(`${name} = SkillProgress(${q(id)}, ${q(metric)})`);
      return name;
    });

  if (ranking.improving.length > 0) {
    card(
      "up",
      "progress",
      t.progressTitle,
      t.progressBody,
      skillFigures("u", ranking.improving, "change"),
    );
  }
  if (ranking.needsAttention.length > 0) {
    const figures = skillFigures("a", ranking.needsAttention, "mastery");
    card("slow", "attention", t.attentionTitle, t.attentionBody, figures);
  }
  if (ranking.alerts.length > 0) {
    const figures = [...new Set(ranking.alerts)].map((reason, i) => {
      const name = `n${String(i)}`;
      lines.push(`${name} = AlertCount(${q(reason)})`);
      return name;
    });
    card("alerts", "info", t.alertsTitle, t.alertsBody, figures);
  }
  if (cards.length === 0) card("quiet", "info", t.quietTitle, t.quietBody, []);
  return [`root = Cards([${cards.join(", ")}])`, ...lines, ""].join("\n");
}

function q(value: string): string {
  return JSON.stringify(value);
}
